
import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Play, MapPin, Clock, AlertCircle } from "lucide-react";
import AdPanel from "@/components/ads/AdPanel";
import RightSideMenu from "@/components/navigation/RightSideMenu";
import EnhancedStoryViewer from "@/components/stories/EnhancedStoryViewer";
import StoryVideoUpload from "@/components/stories/StoryVideoUpload";
import StoryGifCropper from "@/components/stories/StoryGifCropper";
import { useToast } from "@/hooks/use-toast";
import { storiesService, Story } from "@/services/storiesService";
import { useGeolocation } from "@/hooks/useGeolocation";

const Stories = () => {
  const [stories, setStories] = useState<Story[]>([]);
  const [userStories, setUserStories] = useState<Story[]>([]);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [selectedStoryForCrop, setSelectedStoryForCrop] = useState<Story | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { location, error: locationError, requestLocation } = useGeolocation();

  // Load stories on component mount and location change
  useEffect(() => {
    loadStories();
    loadUserStories();
  }, [location]);

  const loadStories = async () => {
    try {
      if (location) {
        // Convert location format for stories service
        const storiesData = await storiesService.getStoriesByDistance(
          location.lat,
          location.lng,
          100 // 100km radius
        );
        setStories(storiesData);
      }
    } catch (error: any) {
      console.error('Error loading stories:', error);
      toast({
        title: "Error",
        description: "Failed to load stories",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserStories = async () => {
    try {
      const userStoriesData = await storiesService.getUserStories();
      setUserStories(userStoriesData);
    } catch (error: any) {
      console.error('Error loading user stories:', error);
    }
  };

  const handleStoryClick = (storyId: string) => {
    const storyIndex = stories.findIndex(story => story.id === storyId);
    if (storyIndex !== -1) {
      setCurrentStoryIndex(storyIndex);
      setIsViewerOpen(true);
    }
  };

  const handleCreateStory = () => {
    if (!location) {
      requestLocation();
      toast({
        title: "Location Required",
        description: "Please allow location access to upload stories",
        variant: "destructive",
      });
      return;
    }
    setIsUploadOpen(true);
  };

  const handleUploadSuccess = (storyId: string) => {
    loadUserStories();
    toast({
      title: "Story Uploaded!",
      description: "Your story is being reviewed. Create a GIF preview now!",
    });
    
    // Find the uploaded story and open cropper
    setTimeout(async () => {
      const userStoriesData = await storiesService.getUserStories();
      const newStory = userStoriesData.find(s => s.id === storyId);
      if (newStory) {
        setSelectedStoryForCrop(newStory);
        setIsCropperOpen(true);
      }
    }, 1000);
  };

  const handleGifCreated = () => {
    loadUserStories();
    toast({
      title: "Profile GIF Created!",
      description: "Your GIF will appear in Discovery once your story is approved",
    });
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const storyTime = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - storyTime.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else {
      return `${Math.floor(diffInMinutes / 60)}h ago`;
    }
  };

  const getStoryStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-400';
      case 'rejected': return 'text-neon-red';
      default: return 'text-yellow-400';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-carbon via-carbon to-electric-violet/10">
      <RightSideMenu />
      <AdPanel />
      
      <div className="p-4 pb-20">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1488590528505-98d5b5aba04b?w=400&h=300&fit=crop" 
                alt="Stories feature" 
                className="w-full h-48 object-cover rounded-lg mb-4 border border-electric-violet/30"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-carbon/60 to-transparent rounded-lg" />
            </div>
            <h1 className="text-2xl font-bold text-ice-white mb-2">Stories</h1>
            <p className="text-ice-white/70">Share 15-second videos with nearby users</p>
          </div>

          {/* Location Status */}
          {locationError && (
            <Card className="mb-4 border-yellow-400/30 bg-yellow-400/10">
              <CardContent className="p-4">
                <div className="flex items-center space-x-2 text-yellow-400">
                  <AlertCircle className="w-4 h-4" />
                  <div>
                    <p className="text-sm font-medium">Location Access Required</p>
                    <p className="text-xs opacity-70">Enable location to see nearby stories and upload your own</p>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="mt-2 border-yellow-400 text-yellow-400 hover:bg-yellow-400/10"
                      onClick={requestLocation}
                    >
                      <MapPin className="w-3 h-3 mr-1" />
                      Enable Location
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Create Story Button */}
          <Card className="mb-6 border-electric-violet/30 bg-electric-violet/5">
            <CardContent className="p-4">
              <Button 
                onClick={handleCreateStory}
                className="w-full bg-gradient-to-r from-electric-violet to-neon-red hover:from-electric-violet/80 hover:to-neon-red/80"
                disabled={!location}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your Story
              </Button>
            </CardContent>
          </Card>

          {/* Your Stories */}
          {userStories.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-ice-white mb-3">Your Stories</h3>
              <div className="space-y-3">
                {userStories.map((story) => (
                  <Card key={story.id} className="border-electric-violet/30 bg-carbon/50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-16 bg-black rounded overflow-hidden">
                            <video
                              src={story.media_url}
                              className="w-full h-full object-cover"
                              muted
                            />
                          </div>
                          <div>
                            <p className={`text-sm font-medium ${getStoryStatusColor(story.status)}`}>
                              {story.status.charAt(0).toUpperCase() + story.status.slice(1)}
                            </p>
                            <p className="text-xs text-ice-white/50">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {formatTimeAgo(story.created_at)}
                            </p>
                            {story.rejection_reason && (
                              <p className="text-xs text-neon-red mt-1">{story.rejection_reason}</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex space-x-2">
                          {story.status === 'approved' && !story.gif_preview_url && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-electric-violet text-electric-violet"
                              onClick={() => {
                                setSelectedStoryForCrop(story);
                                setIsCropperOpen(true);
                              }}
                            >
                              Create GIF
                            </Button>
                          )}
                          {story.gif_preview_url && (
                            <div className="text-xs text-green-400">✓ GIF Ready</div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Nearby Stories */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-ice-white">
              Nearby Stories {location && `(${stories.length})`}
            </h3>
            
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-electric-violet mx-auto"></div>
                <p className="text-ice-white/50 mt-2">Loading stories...</p>
              </div>
            ) : stories.length === 0 ? (
              <Card className="border-electric-violet/30">
                <CardContent className="p-8 text-center">
                  <Play className="w-16 h-16 text-electric-violet/50 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-ice-white mb-2">No Stories Yet</h3>
                  <p className="text-ice-white/60 mb-4">
                    {location ? 'No active stories in your area' : 'Enable location to see nearby stories'}
                  </p>
                  <Button onClick={handleCreateStory} variant="outline" className="border-electric-violet text-electric-violet">
                    Be the First to Share
                  </Button>
                </CardContent>
              </Card>
            ) : (
              stories.map((story) => (
                <Card 
                  key={story.id}
                  className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-[1.02] border-electric-violet/30 bg-carbon/50"
                  onClick={() => handleStoryClick(story.id)}
                >
                  <CardContent className="p-0">
                    <div className="flex items-center p-4">
                      <div className="relative">
                        <img
                          src={story.user_avatar}
                          alt={story.user_name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-electric-violet"
                        />
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-electric-violet rounded-full flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        </div>
                      </div>
                      
                      <div className="ml-3 flex-1">
                        <p className="font-semibold text-ice-white">{story.user_name}</p>
                        <p className="text-sm text-ice-white/50">
                          {story.distance_km ? `${story.distance_km.toFixed(1)}km away` : 'Nearby'} • {formatTimeAgo(story.created_at)}
                        </p>
                      </div>
                      
                      <Play className="w-5 h-5 text-electric-violet" />
                    </div>
                    
                    <div className="px-4 pb-4">
                      <div className="w-full h-32 bg-black rounded-lg overflow-hidden">
                        <video
                          src={story.media_url}
                          className="w-full h-full object-cover"
                          muted
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Story Viewer Modal */}
      <EnhancedStoryViewer
        stories={stories}
        currentStoryIndex={currentStoryIndex}
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        onStoryChange={setCurrentStoryIndex}
      />

      {/* Story Upload Modal */}
      <StoryVideoUpload
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        onUploadSuccess={handleUploadSuccess}
        userLocation={location}
      />

      {/* GIF Cropper Modal */}
      {selectedStoryForCrop && (
        <StoryGifCropper
          isOpen={isCropperOpen}
          onClose={() => {
            setIsCropperOpen(false);
            setSelectedStoryForCrop(null);
          }}
          story={selectedStoryForCrop}
          onGifCreated={handleGifCreated}
        />
      )}
    </div>
  );
};

export default Stories;
